import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import type { BaseIconProps } from "@/assets/icons/utils";
import { withGitpodSize } from "@/assets/icons/utils";
import { cn } from "@/components/podkit/lib/cn";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { Tooltip } from "@/components/Tooltip";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { IconCrossSmall } from "central-icons/IconCrossSmall";
import * as React from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type FC, type ReactNode } from "react";

const CloseIcon = withGitpodSize(IconCrossSmall);

export type TabSize = "sm" | "md";
const TabSizeContext = createContext<TabSize>("md");

// NOTE: This component is intentionally marked @deprecated to signal it's not ready for consumption.
// Remove the tag when Conversation v2 UI work begins.

/**
 * Underline-style tabs — a flat tab bar with a sliding bottom-border indicator.
 * Use for page-level or panel-level navigation where the tab bar sits flush
 * against a content area (e.g. conversation / code / file panels).
 *
 * Built on Radix Tabs for keyboard navigation and ARIA semantics.
 *
 * @deprecated Not yet available for use — coming soon for Conversation v2 UI.
 */

const NavigationTabs = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Root ref={ref} activationMode="manual" className={cn("flex flex-col", className)} {...props} />
));
NavigationTabs.displayName = "NavigationTabs";

type IndicatorStyle = { left: number; width: number };

/** @deprecated Not yet available for use — coming soon for Conversation v2 UI. */
const NavigationTabsList = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.List>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & { size?: TabSize }
>(({ className, children, size = "md", ...props }, ref) => {
    const innerRef = useRef<HTMLDivElement>(null);
    const [indicator, setIndicator] = useState<IndicatorStyle | null>(null);
    // Skip the transition on the very first render so the bar appears instantly.
    const hasAnimated = useRef(false);

    React.useImperativeHandle(ref, () => innerRef.current!);

    const updateIndicator = useCallback(() => {
        const list = innerRef.current;
        if (!list) return;

        const active = list.querySelector<HTMLElement>('[data-state="active"]');
        if (!active) {
            setIndicator(null);
            return;
        }

        setIndicator({
            left: active.offsetLeft,
            width: active.offsetWidth,
        });
    }, []);

    useEffect(() => {
        const list = innerRef.current;
        if (!list) return;

        // Initial position.
        updateIndicator();

        // Radix toggles data-state on the triggers when the active tab changes.
        const observer = new MutationObserver(updateIndicator);
        observer.observe(list, { attributes: true, attributeFilter: ["data-state"], subtree: true });

        // Recompute on resize (font loading, container changes, etc.).
        // Observe the list and every trigger so the indicator updates when
        // the active trigger resizes due to a text content change.
        // Re-runs when children change so dynamically added triggers are observed.
        const resizeObserver = new ResizeObserver(updateIndicator);
        resizeObserver.observe(list);
        for (const trigger of list.querySelectorAll<HTMLElement>('[role="tab"]')) {
            resizeObserver.observe(trigger);
        }

        return () => {
            observer.disconnect();
            resizeObserver.disconnect();
        };
    }, [updateIndicator, children]);

    // After the first real position is set, enable transitions.
    useEffect(() => {
        if (indicator) {
            requestAnimationFrame(() => {
                hasAnimated.current = true;
            });
        }
    }, [indicator]);

    // When children change (e.g. tab removed), snap the indicator
    // to the new position without animating to avoid a janky slide.
    const prevChildCount = useRef<number | null>(null);
    useEffect(() => {
        const count = React.Children.count(children);
        if (prevChildCount.current !== null && prevChildCount.current !== count) {
            hasAnimated.current = false;
            updateIndicator();
            requestAnimationFrame(() => {
                hasAnimated.current = true;
            });
        }
        prevChildCount.current = count;
    }, [children, updateIndicator]);

    return (
        <TabSizeContext.Provider value={size}>
            <TabsPrimitive.List
                ref={innerRef}
                className={cn(
                    "relative flex items-center gap-1",
                    size === "sm" ? "h-8" : "h-10",
                    // Full-width bottom rule via pseudo-element so the active indicator can layer above it.
                    "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-border-base",
                    className,
                )}
                {...props}
            >
                {children}
                {indicator && (
                    <div
                        aria-hidden
                        className="pointer-events-none absolute bottom-0 z-10 h-0.5 bg-content-primary"
                        style={{
                            left: indicator.left,
                            width: indicator.width,
                            transition: hasAnimated.current ? "left 200ms ease, width 200ms ease" : "none",
                        }}
                    />
                )}
            </TabsPrimitive.List>
        </TabSizeContext.Provider>
    );
});
NavigationTabsList.displayName = "NavigationTabsList";

/** @deprecated Not yet available for use — coming soon for Conversation v2 UI. */
type NavigationTabsTriggerProps = {
    LeadingIcon?: React.ComponentType<BaseIconProps>;
    /** When provided, rendered after the label text (e.g. a Counter badge). */
    counter?: ReactNode;
    /** When provided, a close button appears on hover/focus. Clicking it fires this callback without activating the tab. */
    onClose?: () => void;
    /** When provided, right-clicking the tab opens a context menu with these items. Pass ContextMenuItem / ContextMenuSeparator elements. */
    contextMenuContent?: ReactNode;
} & React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>;

/** @deprecated Not yet available for use — coming soon for Conversation v2 UI. */
const NavigationTabsTrigger = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Trigger>,
    NavigationTabsTriggerProps
>(({ className, LeadingIcon, counter, onClose, contextMenuContent, children, ...props }, ref) => {
    const size = useContext(TabSizeContext);
    const iconElement = LeadingIcon ? <LeadingIcon size="sm" aria-hidden={true} className="shrink-0" /> : null;
    const label = typeof children === "string" ? children : undefined;
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const chevronRef = useRef<HTMLSpanElement | null>(null);
    const labelRef = useRef<HTMLSpanElement | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuOpenedViaKeyboard, setMenuOpenedViaKeyboard] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);

    // Detect whether the label text is truncated by comparing scroll vs client width.
    useEffect(() => {
        const el = labelRef.current;
        if (!el) return;

        const check = () => setIsTruncated(el.scrollWidth > el.clientWidth);
        check();

        const observer = new ResizeObserver(check);
        observer.observe(el);
        return () => observer.disconnect();
    }, [children]);

    const hasMenu = !!contextMenuContent;
    const hasClose = !!onClose;

    const actionIconStyles = cn(
        "flex items-center justify-center rounded-sm",
        "outline-none focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-border-brand",
    );

    // Context menu chevron — overlaid inside the pill (only used when there's
    // a menu but no close button, e.g. the conversation tab).
    const menuOverlay = hasMenu && !hasClose && (
        <span
            className={cn(
                "absolute bottom-px right-px top-px flex items-center gap-0.5 pl-4 pr-1",
                "rounded-r-md bg-[image:linear-gradient(to_right,transparent,var(--tab-fade)_40%,var(--tab-fade))]",
                "opacity-0 transition-opacity",
                "group-hover/tab:opacity-100 group-focus-visible/tab:opacity-100",
                menuOpen && "opacity-100",
            )}
        >
            <DropdownMenuTrigger asChild>
                <span
                    ref={chevronRef}
                    tabIndex={-1}
                    aria-hidden="true"
                    className={cn(
                        "text-content-tertiary hover:text-content-primary",
                        menuOpen && "text-content-primary",
                        actionIconStyles,
                    )}
                    data-tracking-id="tab-options"
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <IconChevronDownSmall size="sm" />
                </span>
            </DropdownMenuTrigger>
        </span>
    );

    // Close button — inline in the flow, always visible on the active tab,
    // shown on hover/focus for inactive tabs.
    const closeButton = hasClose && (
        <span
            role="button"
            tabIndex={-1}
            className={cn(
                "shrink-0 text-content-tertiary transition-opacity",
                "hover:text-content-primary",
                "opacity-0 group-hover/tab:opacity-100 group-focus-visible/tab:opacity-100",
                "group-data-[state=active]/tab:opacity-100",
                actionIconStyles,
            )}
            data-tracking-id="tab-close"
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onClose();
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label={label ? `Close ${label}` : "Close tab"}
        >
            <CloseIcon size="sm" />
        </span>
    );

    const trigger = (
        <TabsPrimitive.Trigger
            ref={(node) => {
                triggerRef.current = node;
                if (typeof ref === "function") ref(node);
                else if (ref) ref.current = node;
            }}
            className={cn(
                "group/tab relative flex h-full max-w-60 shrink-0 items-center font-medium transition-colors",
                size === "sm" ? "text-sm" : "text-base",
                "focus-visible:outline-none focus-visible:ring-0",
                "text-content-strong hover:text-content-primary",
                "data-[state=active]:text-content-primary",
                "disabled:pointer-events-none disabled:opacity-50",
                menuOpen && "text-content-primary",
                className,
            )}
            {...props}
            onKeyDown={(e) => {
                props.onKeyDown?.(e);
                if ((e.key === "Delete" || e.key === "Backspace") && onClose) {
                    e.stopPropagation();
                    e.preventDefault();
                    onClose();
                } else if (e.key === "ArrowDown" && hasMenu) {
                    e.stopPropagation();
                    e.preventDefault();
                    setMenuOpenedViaKeyboard(true);
                    setMenuOpen(true);
                }
            }}
            onMouseDown={(e) => {
                // Prevent default middle-click auto-scroll behavior.
                if (e.button === 1 && onClose) {
                    e.preventDefault();
                }
            }}
            onAuxClick={(e) => {
                if (e.button === 1 && onClose) {
                    e.preventDefault();
                    e.stopPropagation();
                    onClose();
                }
            }}
        >
            <span
                className={cn(
                    "relative flex min-w-0 items-center overflow-hidden rounded-md transition-colors",
                    size === "sm" ? "gap-1 px-1.5 py-1" : "gap-1.5 px-2 py-1.5",
                    "group-hover/tab:bg-surface-02",
                    menuOpen && "bg-surface-02",
                    menuOpenedViaKeyboard && "outline outline-1 -outline-offset-1 outline-border-brand",
                    "group-focus-visible/tab:bg-surface-02 group-focus-visible/tab:outline group-focus-visible/tab:outline-1 group-focus-visible/tab:-outline-offset-1 group-focus-visible/tab:outline-border-brand",
                )}
                style={
                    {
                        // Opaque composited color of surface-02 on surface-primary, used
                        // by the menu-overlay gradient to fully occlude truncated label
                        // text. surface-02 is semi-transparent in dark mode so a raw
                        // gradient through it cannot hide the text underneath.
                        "--tab-fade":
                            "color-mix(in srgb, rgb(var(--surface-muted)) calc(var(--surface-muted-opacity) * 100%), rgb(var(--surface-primary)))",
                    } as React.CSSProperties
                }
            >
                {iconElement}
                <Tooltip content={isTruncated ? label : undefined}>
                    <span ref={labelRef} className="truncate">
                        {children}
                    </span>
                </Tooltip>
                {counter}
                {closeButton}
                {menuOverlay}
            </span>
        </TabsPrimitive.Trigger>
    );

    // Compute the horizontal offset to left-align the menu with the tab trigger
    // instead of the chevron icon (which is the Radix anchor).
    const getAlignOffset = useCallback(() => {
        if (!triggerRef.current || !chevronRef.current) return 0;
        const tabRect = triggerRef.current.getBoundingClientRect();
        const chevronRect = chevronRef.current.getBoundingClientRect();
        return tabRect.left - chevronRect.left;
    }, []);

    if (!hasMenu) {
        return trigger;
    }

    return (
        <DropdownMenu
            open={menuOpen}
            onOpenChange={(open) => {
                setMenuOpen(open);
            }}
        >
            <span
                className={cn("flex h-full max-w-60 shrink-0 items-center", className)}
                onContextMenu={(e) => {
                    e.preventDefault();
                    setMenuOpen(true);
                }}
            >
                {trigger}
            </span>
            <DropdownMenuContent
                align="start"
                alignOffset={getAlignOffset()}
                onKeyDown={(e) => {
                    if (e.key === "ArrowUp") {
                        const content = e.currentTarget;
                        const items = content.querySelectorAll("[role='menuitem']");
                        if (items.length > 0 && document.activeElement === items[0]) {
                            e.preventDefault();
                            setMenuOpen(false);
                        }
                    }
                }}
                onCloseAutoFocus={(e) => {
                    e.preventDefault();
                    triggerRef.current?.focus();
                    // Defer clearing the keyboard flag so the outline persists
                    // through the frame where focus transfers to the tab trigger,
                    // avoiding a single-frame flash where neither outline source
                    // (menuOpenedViaKeyboard nor :focus-visible) is active.
                    requestAnimationFrame(() => setMenuOpenedViaKeyboard(false));
                }}
            >
                {contextMenuContent}
                {onClose && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" data-tracking-id="tab-close-menu" onClick={onClose}>
                            Close
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
});
NavigationTabsTrigger.displayName = "NavigationTabsTrigger";

/** @deprecated Not yet available for use — coming soon for Conversation v2 UI. */
const NavigationTabsContent = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Content
        ref={ref}
        className={cn(
            "focus-visible:outline-none focus-visible:ring-0",
            "data-[state=active]:duration-200 data-[state=active]:animate-in data-[state=active]:fade-in-0",
            className,
        )}
        {...props}
    />
));
NavigationTabsContent.displayName = "NavigationTabsContent";

/** Vertical divider placed between tab triggers to visually group them. */
const NavigationTabsSeparator: FC<{ className?: string }> = ({ className }) => (
    <div aria-hidden className={cn("mx-1 h-4 w-px shrink-0 self-center bg-border-subtle", className)} />
);
NavigationTabsSeparator.displayName = "NavigationTabsSeparator";

const NavigationTabsCompound = Object.assign(NavigationTabs, {
    List: NavigationTabsList,
    Trigger: NavigationTabsTrigger,
    Content: NavigationTabsContent,
    Separator: NavigationTabsSeparator,
});

export { NavigationTabsCompound as NavigationTabs };
export type { NavigationTabsTriggerProps };
